import { getCurationsMe as getCurationsMeApi } from '@musinsa/apis-curator';
import { lazyPipe } from '@musinsa/packages-functional';
import {
  validateServiceRequest,
  validateServiceResponse,
  withServiceErrorCapture,
} from '@musinsa/services-common-service-zod4';

import type {
  GetCurationsMeServiceRequest,
  GetCurationsMeServiceResponse,
} from './get-curations-me.dto';
import {
  GetCurationsMeServiceRequestSchema,
  GetCurationsMeServiceResponseSchema,
} from './get-curations-me.dto';

const toMyCurationType = (value: string | null | undefined): 'GOODS' | 'SNAP2' | null => {
  if (value === 'GOODS' || value === 'SNAP2') return value;
  return null;
};

export const getCurationsMeService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(GetCurationsMeServiceRequestSchema),
    async (request: GetCurationsMeServiceRequest): Promise<GetCurationsMeServiceResponse> => {
      const apiResponse = await getCurationsMeApi(request);

      if (apiResponse.meta?.result === 'FAIL') {
        throw new Error(apiResponse.meta?.message ?? '큐레이터 상품 조회에 실패했습니다.');
      }

      const hasNextPage = apiResponse.link?.next !== null && apiResponse.link?.next !== undefined;
      const nextPage = hasNextPage ? request.page + 1 : undefined;

      const transformedList = apiResponse.data.list.map((item) => ({
        curationId: item.curationId,
        title: item.title,
        description: item.description ?? null,
        type: toMyCurationType(item.type),
        ordNo: item.ordNo,
        isPublished: item.isPublished ?? null,
        snapItems: item.snapItems,
        goodsItems: item.goodsItems.map(
          ({
            salesRate,
            isLiked,
            likeCount,
            reviewScore,
            reviewCount,
            existsCoupon,
            totalQuantity,
            extraDiscountAmount,
            finalPrice,
            finalDiscount,
            isLowestPrice,
            isExclusive,
            ...goodsRest
          }) => ({
            ...goodsRest,
            salesRate: salesRate ?? 0,
            isLiked: isLiked ?? false,
            likeCount: likeCount ?? 0,
            reviewScore: reviewScore ?? '0',
            reviewCount: reviewCount ?? 0,
            existsCoupon: existsCoupon ?? false,
            totalQuantity: totalQuantity ?? 0,
            extraDiscountAmount: extraDiscountAmount ?? 0,
            finalPrice: finalPrice ?? goodsRest.couponPrice ?? goodsRest.price,
            finalDiscount: finalDiscount ?? goodsRest.couponSalesRate ?? salesRate ?? 0,
            isLowestPrice: isLowestPrice ?? false,
            isExclusive: isExclusive ?? false,
            hasNextPage,
            nextPage,
          }),
        ),
      }));

      return {
        total:
          apiResponse.link && 'total' in apiResponse.link
            ? ((apiResponse.link as { total: number | null }).total ?? 0)
            : 0,
        list: transformedList,
        pagination: {
          currentPage: request.page,
          pageSize: request.size,
          hasNextPage,
          nextPage,
        },
      };
    },
    validateServiceResponse(GetCurationsMeServiceResponseSchema),
  ),
);
